import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiClock, FiDollarSign, FiTarget, FiTrendingUp } = FiIcons;

const Differentiators = () => {
  const differentiators = [
    {
      icon: FiAward,
      title: 'Specialized Expertise',
      description: 'Our team consists exclusively of certified AML/CFT professionals with extensive regulatory experience across multiple jurisdictions.',
      metric: '15+ Years',
      metricLabel: 'Average Experience'
    },
    {
      icon: FiTarget,
      title: 'Tailored Approach',
      description: 'Every solution is custom-designed for your specific business model, risk profile, and regulatory environment.',
      metric: '100%',
      metricLabel: 'Custom Solutions'
    },
    {
      icon: FiClock,
      title: 'Rapid Deployment',
      description: 'Get compliant fast with our proven implementation methodology that minimizes disruption to your operations.',
      metric: '30 Days',
      metricLabel: 'Implementation Time'
    },
    {
      icon: FiDollarSign,
      title: 'Cost-Effective',
      description: 'Access senior-level expertise at a fraction of the cost of hiring full-time compliance staff.',
      metric: '60%',
      metricLabel: 'Cost Savings vs FTE'
    },
    {
      icon: FiUsers,
      title: 'Dedicated Support',
      description: 'Your assigned MLRO provides personalized attention and is available for urgent compliance matters.',
      metric: '24/7',
      metricLabel: 'Urgent Support'
    },
    {
      icon: FiTrendingUp,
      title: 'Proven Track Record',
      description: 'Successfully helped 200+ companies achieve and maintain compliance without regulatory issues.',
      metric: '200+',
      metricLabel: 'Successful Clients'
    }
  ];

  return (
    <section id="differentiators" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Why Choose <span className="text-emerald-700">vMLRO</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We don't just provide compliance services – we deliver peace of mind through 
            expertise, reliability, and results that speak for themselves.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {differentiators.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative bg-gradient-to-br from-gray-50 to-emerald-50 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-emerald-500 group"
            >
              <div className="absolute top-6 right-6 w-20 h-20 bg-emerald-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
              
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-green-700 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={item.icon} className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">{item.description}</p>

                <div className="bg-white rounded-xl p-5 border-2 border-emerald-200 shadow-sm">
                  <div className="text-3xl font-bold text-emerald-700 mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {item.metric}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">{item.metricLabel}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-emerald-600 to-green-700 rounded-3xl p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="relative text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Trusted by Industry Leaders
            </h3>
            <p className="text-emerald-100 text-lg max-w-2xl mx-auto">
              Join companies that have successfully navigated regulatory challenges with our expert guidance.
            </p>
          </div>

          <div className="relative grid md:grid-cols-4 gap-6">
            {[
              { value: '100%', label: 'Compliance Success Rate' },
              { value: '0', label: 'Regulatory Penalties' },
              { value: '98%', label: 'Client Retention Rate' },
              { value: '24h', label: 'Average Response Time' }
            ].map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {stat.value}
                </div>
                <div className="text-emerald-100 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Differentiators;